import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

type LanguageSwitcherProps = {
  /** Header bar: uses crm-app-header__lang-trigger (no shadcn border/size defaults) */
  variant?: 'default' | 'header';
};

/**
 * Language switcher component that allows users to change the application language
 */
function LanguageSwitcher({ variant = 'default' }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation('common');

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
  };

  const currentLanguage = LANGUAGES.find((lang) => lang.code === i18n.language);

  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger
        size={variant === 'header' ? 'sm' : 'default'}
        className={cn(
          variant === 'header'
            ? 'crm-app-header__lang-trigger text-xs'
            : 'h-7 w-fit gap-1.5 rounded-md px-2 text-xs xl:w-[140px]',
        )}
      >
        <SelectValue placeholder={t('languageSelect.selectLanguage')}>
          {currentLanguage && (
            <span className="flex items-center gap-1.5 text-xs leading-none">
              <span className="text-xs leading-none">{currentLanguage.flag}</span>
              <span className="hidden text-xs font-medium xl:block">{currentLanguage.name}</span>
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map((language) => (
          <SelectItem key={language.code} value={language.code} className="text-xs">
            <span className="flex items-center gap-1.5">
              <span className="text-sm leading-none">{language.flag}</span>
              <span>{language.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export { LanguageSwitcher };
